import { ReactNode, useId } from 'react'
import { AnySchema } from 'yup'
import * as LabelPrimitive from '@radix-ui/react-label'
import { Slot } from 'radix-ui'

import { useFieldHelpers } from '@/base'
import { Label, Text, TextProps } from '../../..'
import { mtc, TypographyStyles } from '../../../utils/style-helpers'
import { Banner, BannerVariants, BaseBannerProps } from '../banner'
import { bannerStyles, descriptionStyles } from './styles'

import { FormInput } from './input'
import { FormTextarea } from './textarea'
import { FormSelect } from './select'
import { FormMultiselect } from './multiselect'
import { FormRadioGroup } from './radio-group'
import { FormCheckbox } from './checkbox'
import { FormAutocomplete } from './autocomplete'

export interface LabelProps extends LabelPrimitive.LabelProps {
  children?: ReactNode
  className?: string
  disabled?: boolean
  error?: boolean
  optionalText?: string
  title?: string
  name: string
  schema?: AnySchema
  formItemId: string
}

export const FormLabel = ({
  className,
  optionalText,
  title,
  name,
  schema,
  formItemId,
  ...props
}: LabelProps) => {
  const { error } = useFieldHelpers({ name, schema })
  const hasErrors = error || Object.keys(error || {}).length > 0

  return (
    <Label
      className={className}
      error={Boolean(hasErrors)}
      htmlFor={formItemId}
      optionalText={optionalText}
      title={title}
      {...props}
    />
  )
}

export interface FormDescriptionProps extends TextProps {
  disabled?: boolean
  name: string
  schema?: AnySchema
}

export const FormDescription = ({
  className,
  disabled,
  name,
  schema,
  ...props
}: FormDescriptionProps) => {
  const { error } = useFieldHelpers({ name, schema })
  const hasError = Boolean(error || Object.keys(error || {}).length > 0)

  return (
    <Text
      className={mtc(
        descriptionStyles({ error: hasError, disabled }),
        className
      )}
      variant={TypographyStyles.BODY_XS}
      {...props}
    />
  )
}

export interface FormBannerProps extends BaseBannerProps {
  disabled?: boolean
  message?: string
  error?: string
  name: string
  schema?: AnySchema
  errorTestId?: string
}

export const FormBanner = ({
  id,
  className,
  disabled,
  message,
  name,
  schema,
  errorTestId,
}: FormBannerProps) => {
  const { error } = useFieldHelpers({ name, schema })
  const text = error || message
  const variant = error ? BannerVariants.DANGER : BannerVariants.DEFAULT
  const isVisible = (error || message) && !disabled

  if (!isVisible) {
    return null
  }
  return (
    <Banner
      className={mtc(bannerStyles(), className)}
      id={id}
      text={text}
      variant={variant}
      wrapperClassName="mt-2 w-full"
      {...(errorTestId ? { 'data-testid': errorTestId } : {})}
    />
  )
}

const useFormIds = () => {
  const id = useId()

  return {
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formBannerId: `${id}-form-item-banner`,
  }
}

export interface FormFieldProps {
  children?: ReactNode
  className?: string
  name: string
  label?: string
  optionalText?: string
  description?: string | ReactNode
  message?: string
  schema?: AnySchema
  errorTestId?: string
}

export const FormField = ({
  children,
  className,
  name,
  label,
  optionalText,
  description,
  message,
  schema,
  errorTestId,
}: FormFieldProps) => {
  const { error } = useFieldHelpers({ name, schema })
  const { formItemId, formDescriptionId, formBannerId } = useFormIds()
  return (
    <div className={className}>
      {label && (
        <FormLabel
          title={label}
          optionalText={optionalText}
          name={name}
          schema={schema}
          formItemId={formItemId}
        />
      )}

      {description && (
        <FormDescription id={formDescriptionId} name={name} schema={schema}>
          {description}
        </FormDescription>
      )}

      <Slot.Root
        aria-label={name}
        aria-describedby={
          !error
            ? `${formDescriptionId}`
            : `${formDescriptionId} ${formBannerId}`
        }
        id={formItemId}
        aria-invalid={Boolean(error)}
      >
        {children}
      </Slot.Root>

      <FormBanner
        id={formBannerId}
        error={error}
        message={message}
        name={name}
        schema={schema}
        errorTestId={errorTestId}
      />
    </div>
  )
}

export const Form = {
  Field: FormField,
  Input: FormInput,
  Textarea: FormTextarea,
  Select: FormSelect,
  Multiselect: FormMultiselect,
  RadioGroup: FormRadioGroup,
  Checkbox: FormCheckbox,
  Autocomplete: FormAutocomplete,
}
